import {
  Typography,
  type SxProps,
  type Theme,
  type TypographyProps,
} from '@mui/material';
import {
  BODY2_LINE_HEIGHT,
  getDenseResponsiveFontSize,
} from 'theme/watchdutyTheme.utils';

/** Secondary metadata on geo-event cards — body2 color; scales unless `dense`. */
export const geoEventMetadataTypographyProps: Pick<
  TypographyProps,
  'variant' | 'color' | 'component'
> = {
  variant: 'body2',
  color: 'text.secondary',
  component: 'span',
};

const geoEventDenseMetadataTypographySx: SxProps<Theme> = {
  fontSize: getDenseResponsiveFontSize('0.875rem'),
  lineHeight: BODY2_LINE_HEIGHT,
  '& b': {
    fontWeight: 700,
    lineHeight: 'inherit',
  },
};

const geoEventDenseMetadataTruncateSx: SxProps<Theme> = {
  ...geoEventDenseMetadataTypographySx,
  display: 'block',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export type GeoEventMetadataTextProps = {
  children: React.ReactNode;
  /** Fixed design size for search rows and dense metadata. Default true. */
  dense?: boolean;
  /** Single-line ellipsis when overflow. Default true when dense. */
  truncate?: boolean;
  sx?: SxProps<Theme>;
  'data-testid'?: string;
} & Pick<TypographyProps, 'fontWeight'>;

export const GeoEventMetadataText = (
  props: GeoEventMetadataTextProps,
): JSX.Element => {
  const {
    children,
    dense = true,
    truncate = dense,
    sx,
    fontWeight,
    'data-testid': dataTestId,
  } = props;

  let denseSx: SxProps<Theme> | undefined;
  if (dense) {
    denseSx = truncate
      ? geoEventDenseMetadataTruncateSx
      : geoEventDenseMetadataTypographySx;
  }

  let typographySx: SxProps<Theme> | undefined;
  if (denseSx && sx) {
    typographySx = [denseSx, sx] as SxProps<Theme>;
  } else {
    typographySx = denseSx ?? sx;
  }

  return (
    <Typography
      {...geoEventMetadataTypographyProps}
      fontWeight={fontWeight}
      data-testid={dataTestId}
      sx={typographySx}
    >
      {children}
    </Typography>
  );
};
